/**
 * Landing — главная witelab.ru: лендинг-портфолио (13.10.2026, заменил старую заглушку).
 * Старая заглушка удалена. Все тексты страницы собраны
 * в объекте COPY ниже, чтобы владелец мог править слова в одном месте.
 * Страница обезличенная: без «я», «свой», «для себя» и т. п.
 */
import { useEffect, useState } from 'react';
import {
  ArrowDown,
  ArrowUpRight,
  Check,
  Images,
  Layers,
  LayoutGrid,
  ListChecks,
  Lock,
  MessageSquare,
  Music,
  NotebookPen,
  Printer,
  ShoppingBag,
  Sparkles,
  StickyNote,
  Truck,
  Wallet,
} from 'lucide-react';
import '../styles/landing-new.css';

/* ─── Тексты страницы ─────────────────────────────────────────────── */

const COPY = {
  title: 'Witelab — мерч, печать и инструменты для маркетплейсов',

  nav: ['Hikomori', 'Collage Easy', 'meetaihi', 'Ozon Lab', 'Daily Lab'],

  hero: {
    h1: 'Мерч, печать и инструменты для маркетплейсов',
    /* слово с градиентом внутри H1 */
    h1Accent: 'инструменты',
    lead:
      'Witelab — несколько проектов вокруг печати на заказ: магазин аниме-мерча Hikomori, ' +
      'бесплатный редактор коллажей и внутренние сервисы для торговли на Ozon, Wildberries ' +
      'и Яндекс Маркете. Два проекта открыты всем, остальные закрытые.',
    ctaShop: 'В магазин Hikomori',
    ctaProjects: 'Смотреть проекты',
    mascotSticker: 'это Айхи',
    mascotAlt: 'Айхи — маскот магазина Hikomori',
  },

  groups: {
    open: 'Проекты',
    closed: 'Закрытые проекты',
    lock: 'доступ по логину',
  },

  hikomori: {
    num: '01',
    kind: 'магазин',
    name: 'Hikomori',
    domain: 'shop-hiko.ru',
    text:
      'Аниме-мерч с печатью под каждый заказ: сумки-почтальонки, пеналы, кружки, пазлы, ' +
      'подушки и ещё шесть категорий. Печать, упаковка и отправка по всей России — ' +
      'без посредников.',
    /* Фото — реальные товары из «Популярного» на shop-hiko.ru */
    photos: [
      { src: '/landing/hiko-sumka.webp',    caption: 'Сумка-почтальонка', alt: 'Сумка-почтальонка с принтом, Hikomori', tilt: '-4deg' },
      { src: '/landing/hiko-kruzhka.webp',  caption: 'Кружка',            alt: 'Керамическая кружка с принтом, Hikomori', tilt: '3deg' },
      { src: '/landing/hiko-pazl.webp',     caption: 'Пазл',              alt: 'Пазл с принтом, Hikomori', tilt: '2deg' },
      { src: '/landing/hiko-podushka.webp', caption: 'Подушка',           alt: 'Подушка с принтом, Hikomori', tilt: '-3deg' },
    ],
    sticker: 'печать под заказ',
    cta: 'Открыть shop-hiko.ru',
  },

  collage: {
    num: '02',
    kind: 'редактор',
    name: 'Collage Easy',
    domain: 'collage-easy.ru',
    text:
      'Коллаж из фотографий прямо в браузере: что угодно и как угодно, без регистрации ' +
      'и водяных знаков. Готовый коллаж скачивается в хорошем качестве — бесплатно.',
    chips: ['бесплатно', 'без регистрации', 'без водяных знаков'],
    cta: 'Собрать коллаж',
    collageAlt: 'Коллаж из фотографий товаров и портрета Айхи',
  },

  meetaihi: {
    num: '03',
    kind: 'ИИ-студия',
    name: 'meetaihi',
    domain: 'ai.witelab.ru',
    text: 'ИИ-студия: картинки, разбор на слои с выгрузкой в PSD, музыка и подсказки к промптам.',
    features: [
      { icon: Images, text: 'Картинки: Gemini, GPT Image, Grok, Yandex Art, Seedream' },
      { icon: Layers, text: '«Разделить на слои» — и сразу в PSD' },
      { icon: Music, text: 'Музыка: Suno и Lyria' },
      { icon: Sparkles, text: 'Подсказки к промптам' },
    ],
    layers: ['01 · фон', '02 · тени', '03 · Айхи'],
    portraitAlt: 'Айхи, портрет',
  },

  ozon: {
    num: '04',
    kind: 'панель',
    name: 'Ozon Lab',
    domain: 'ozon.witelab.ru',
    text:
      'Панель для работы с маркетплейсами: заказы, макеты, печать, переписка с покупателями, ' +
      'отчёты. Внутренний инструмент.',
    steps: ['заказ', 'макет', 'печать', 'отправка'],
    mockup: 'Mockup Lab — каталог мокапов для карточек товара.',
    mockupDomain: 'mockup.witelab.ru',
    stickerCaption: 'Айхи ищет макет',
    stickerAlt: 'Айхи спрашивает: а где?',
  },

  daily: {
    num: '05',
    kind: 'ежедневник',
    name: 'Daily Lab',
    domain: 'daily.witelab.ru',
    text: 'Ежедневник, заметки, ведение бюджета.',
    rows: [
      { icon: StickyNote, label: 'Заметки', done: true, w: '58%' },
      { icon: ListChecks, label: 'Задачи', done: true, w: '42%' },
      { icon: Wallet, label: 'Бюджет', done: false, w: '66%' },
    ],
  },

  footer: {
    // Владелец: строку «ведёт один человек» убрать (есть сотрудники, которые пользуются сайтами);
    // внизу — коммерческий контакт.
    contactLabel: 'По коммерческим вопросам',
    contactEmail: 'hello@shop-hiko.ru',
    copy: `© ${new Date().getFullYear()} witelab.ru`,
  },
} as const;

const LINKS = {
  hikomori: 'https://shop-hiko.ru',
  collage: 'https://collage-easy.ru',
} as const;

/* Куски «склейки» в секции Collage Easy: позиция, наклон, откуда прилетает, куда уходит на hover */
const COLLAGE_PIECES = [
  { src: '/landing/hiko-podushka.webp', x: '0%',  y: '2%',  w: '46%', r: '-7deg', r2: '-3deg', sx: '-48px', sy: '-36px', hx: '-6px', hy: '-4px', d: '0s' },
  { src: '/landing/hiko-sumka.webp',    x: '52%', y: '0%',  w: '44%', r: '5deg',  r2: '2deg',  sx: '48px',  sy: '-36px', hx: '6px',  hy: '-6px', d: '0.08s' },
  { src: '/landing/hiko-pazl.webp',     x: '4%',  y: '42%', w: '42%', r: '4deg',  r2: '7deg',  sx: '-48px', sy: '40px',  hx: '-6px', hy: '6px',  d: '0.16s' },
  { src: '/landing/hiko-kruzhka.webp',  x: '54%', y: '42%', w: '42%', r: '-4deg', r2: '-8deg', sx: '48px',  sy: '40px',  hx: '6px',  hy: '4px',  d: '0.24s' },
  { src: '/landing/aihi-portrait.webp', x: '32%', y: '31%', w: '36%', r: '2deg',  r2: '-6deg', sx: '0',     sy: '24px',  hx: '0',    hy: '-8px', d: '0.34s', round: true },
] as const;

const ext = { target: '_blank', rel: 'noopener noreferrer' } as const;
/* React 18 не знает fetchPriority в типах — передаём как обычный DOM-атрибут */
const lcpPriority = { fetchpriority: 'high' } as Record<string, string>;
const cssVars = (vars: Record<string, string>) => vars as React.CSSProperties;

const SECTION_IDS = ['hikomori', 'collage', 'meetaihi', 'ozon', 'daily'] as const;

/* ─── Мелкие кусочки ──────────────────────────────────────────────── */

function LockNote() {
  return (
    <span className="ln-lock"><Lock size={12} strokeWidth={2.2} /> {COPY.groups.lock}</span>
  );
}

function SectionHead({
  num, kind, name, domain, href, closed,
}: { num: string; kind: string; name: string; domain: string; href?: string; closed?: boolean }) {
  return (
    <div>
      <div className="ln-eyebrow mb-4"><b>{num}</b> · {kind}</div>
      <h2 className="ln-display ln-h2">{name}</h2>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        {href ? (
          <a href={href} {...ext} className="ln-domain inline-flex items-center min-h-11 -my-3 hover:text-white transition-colors">{domain}</a>
        ) : (
          <span className="ln-domain">{domain}</span>
        )}
        {closed && <LockNote />}
      </div>
    </div>
  );
}

function GroupTitle({ children, note }: { children: string; note?: string }) {
  return (
    <div className="ln-divider mx-auto max-w-6xl px-5 sm:px-8" data-reveal>
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
        <h2 className="ln-display ln-h2-small">{children}</h2>
        {note && <LockNote />}
      </div>
    </div>
  );
}

/* ─── Страница ────────────────────────────────────────────────────── */

export function Landing() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = COPY.title;
    /* Главная всегда тёмная: если в localStorage осталась светлая тема админки,
       не даём её overrides перекрасить лендинг. Админка при входе вернёт свою. */
    document.documentElement.setAttribute('data-theme', 'blue');

    /* Reveal при прокрутке */
    const targets = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal], [data-reveal-group]'));
    let io: IntersectionObserver | undefined;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io?.unobserve(e.target);
          }
        }
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
      targets.forEach((el) => io?.observe(el));
    } else {
      targets.forEach((el) => el.classList.add('is-in'));
    }

    /* Шапка: подложка после прокрутки */
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      document.title = prevTitle;
      io?.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const h1Parts = COPY.hero.h1.split(COPY.hero.h1Accent);

  return (
    <div className="ln">
      {/* Фон */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden>
        <div className="ln-blob ln-blob--red" style={{ width: 720, height: 520, left: '50%', top: -220, transform: 'translateX(-55%)' }} />
        <div className="ln-blob ln-blob--blue" style={{ width: 560, height: 480, right: -180, top: '34%' }} />
        <div className="ln-noise absolute inset-0" />
      </div>

      {/* Шапка */}
      <header className={`ln-header ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between gap-6">
          <a href="#top" className="ln-display flex items-center gap-2.5 text-[15px] min-h-11">
            <img src="/favicon/favicon-96x96.png" alt="" width={26} height={26} className="w-[26px] h-[26px] rounded-md" />
            witelab
          </a>
          <nav className="ln-nav hidden md:flex items-center gap-6" aria-label="Проекты">
            {COPY.nav.map((label, i) => (
              <a key={label} href={`#${SECTION_IDS[i]}`}>{label}</a>
            ))}
          </nav>
        </div>
      </header>

      <main id="top" className="relative z-10">
        {/* ───────────── HERO ───────────── */}
        <section className="relative">
          <div className="ln-dots absolute inset-0 pointer-events-none" aria-hidden />
          <div className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-28 pb-10 sm:pt-32 lg:pt-36 lg:pb-16 grid gap-10 lg:grid-cols-[1.12fr_0.88fr] lg:items-center">
            <div>
              <h1 className="ln-display ln-h1 ln-slide">
                {h1Parts[0]}
                <span className="ln-h1-accent">{COPY.hero.h1Accent}</span>
                {h1Parts[1]}
              </h1>
              <p className="ln-lead ln-rise mt-6 max-w-xl" data-d="1">{COPY.hero.lead}</p>
              <div className="ln-rise mt-8 flex flex-wrap gap-3" data-d="2">
                <a href={LINKS.hikomori} {...ext} className="ln-btn ln-btn--primary">
                  <ShoppingBag size={17} /> {COPY.hero.ctaShop} <ArrowUpRight size={16} className="ln-btn-icon" />
                </a>
                <a href="#hikomori" className="ln-btn ln-btn--ghost">
                  {COPY.hero.ctaProjects} <ArrowDown size={16} />
                </a>
              </div>
            </div>

            {/* Маскот — LCP, без анимации прозрачности */}
            <div className="relative mx-auto w-full max-w-[340px] sm:max-w-[400px] lg:max-w-[460px]">
              <div className="ln-hero-circle absolute left-1/2 top-[54%] w-[78%] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none" aria-hidden />
              <div className="ln-hero-ring absolute left-1/2 top-[54%] w-[92%] aspect-square -translate-x-1/2 -translate-y-1/2 pointer-events-none" aria-hidden />
              <Sparkles className="ln-spark absolute left-[6%] top-[14%] text-white/80" size={22} aria-hidden />
              <Sparkles className="ln-spark absolute right-[4%] top-[34%] text-white/70" size={16} data-d="1" aria-hidden />
              <Sparkles className="ln-spark ln-spark--blue absolute left-[14%] bottom-[16%]" size={18} data-d="2" aria-hidden />
              <img
                src="/landing/aihi-hero.webp"
                srcSet="/landing/aihi-hero.webp 640w, /landing/aihi-hero@2x.webp 920w"
                sizes="(min-width: 1024px) 460px, (min-width: 640px) 400px, 340px"
                width={640}
                height={760}
                alt={COPY.hero.mascotAlt}
                decoding="async"
                {...lcpPriority}
                className="ln-hero-img ln-float relative z-10 w-full h-auto"
              />
              <span className="ln-sticker ln-sticker--red absolute right-[2%] bottom-[8%] z-20" style={cssVars({ '--tilt': '5deg' })}>
                {COPY.hero.mascotSticker}
              </span>
            </div>
          </div>
        </section>

        <GroupTitle>{COPY.groups.open}</GroupTitle>

        {/* ───────────── 01 HIKOMORI: фото слева, текст справа ───────────── */}
        <section id="hikomori" className="ln-sec ln-sec--red scroll-mt-20">
          <div className="ln-shape w-[520px] h-[520px] -left-40 top-1/2 -translate-y-1/2 opacity-70" aria-hidden />
          <div className="relative mx-auto max-w-6xl px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="order-2 lg:order-1 relative mx-auto w-full max-w-[420px]" data-reveal-group>
              <div className="ln-hatch absolute -inset-4 rounded-[32px] opacity-50 pointer-events-none" aria-hidden />
              <div className="ln-polaroids relative grid grid-cols-2 gap-4 sm:gap-6">
                {COPY.hikomori.photos.map((p, i) => (
                  <figure
                    key={p.src}
                    className={`ln-polaroid ${i % 2 ? 'mt-6' : ''}`}
                    data-reveal
                    style={cssVars({ '--tilt': p.tilt, '--d': `${i * 0.1}s` })}
                  >
                    <img src={p.src} alt={p.alt} width={400} height={533} loading="lazy" decoding="async" />
                    <figcaption>{p.caption}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2" data-reveal>
              <SectionHead num={COPY.hikomori.num} kind={COPY.hikomori.kind} name={COPY.hikomori.name} domain={COPY.hikomori.domain} href={LINKS.hikomori} />
              <p className="ln-text mt-6 max-w-lg">{COPY.hikomori.text}</p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a href={LINKS.hikomori} {...ext} className="ln-btn ln-btn--primary">
                  {COPY.hikomori.cta} <ArrowUpRight size={16} className="ln-btn-icon" />
                </a>
                <span className="ln-sticker" style={cssVars({ '--tilt': '-5deg' })}>
                  <Printer size={13} className="inline -mt-0.5 mr-1" />{COPY.hikomori.sticker}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ───────────── 02 COLLAGE EASY: текст слева, склейка справа ───────────── */}
        <section id="collage" className="ln-sec ln-sec--teal ln-sec--right scroll-mt-20">
          <div className="ln-shape w-[560px] h-[560px] -right-48 top-1/2 -translate-y-1/2 opacity-60" aria-hidden />
          <div className="relative mx-auto max-w-6xl px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
            <div data-reveal>
              <SectionHead num={COPY.collage.num} kind={COPY.collage.kind} name={COPY.collage.name} domain={COPY.collage.domain} href={LINKS.collage} />
              <p className="ln-text mt-6 max-w-lg">{COPY.collage.text}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {COPY.collage.chips.map((c) => <span key={c} className="ln-chip"><Check size={13} />{c}</span>)}
              </div>
              <div className="mt-8">
                <a href={LINKS.collage} {...ext} className="ln-btn ln-btn--accent">
                  <LayoutGrid size={16} /> {COPY.collage.cta} <ArrowUpRight size={16} className="ln-btn-icon" />
                </a>
              </div>
            </div>
            <div className="relative mx-auto w-full max-w-[380px] lg:max-w-[440px]">
              <div className="ln-grid-lines absolute -inset-6 rounded-[32px] opacity-60 pointer-events-none" aria-hidden />
              <div className="ln-collage" data-reveal-group role="img" aria-label={COPY.collage.collageAlt}>
                {COLLAGE_PIECES.map((p) => (
                  <div
                    key={p.src}
                    className={`ln-piece ${'round' in p && p.round ? 'ln-piece--round' : ''}`}
                    style={cssVars({ '--x': p.x, '--y': p.y, '--w': p.w, '--r': p.r, '--r2': p.r2, '--sx': p.sx, '--sy': p.sy, '--hx': p.hx, '--hy': p.hy, '--d': p.d })}
                  >
                    <img src={p.src} alt="" width={400} height={'round' in p && p.round ? 400 : 533} loading="lazy" decoding="async" />
                  </div>
                ))}
                <i className="ln-tape" style={{ left: '12%', top: '-1%', rotate: '-8deg' }} aria-hidden />
                <i className="ln-tape" style={{ right: '10%', top: '40%', rotate: '12deg' }} aria-hidden />
              </div>
            </div>
          </div>
        </section>

        <GroupTitle note="lock">{COPY.groups.closed}</GroupTitle>

        {/* ───────────── 03 MEETAIHI: слои слева, текст справа ───────────── */}
        <section id="meetaihi" className="ln-sec ln-sec--violet scroll-mt-20">
          <div className="ln-shape w-[520px] h-[520px] -left-40 top-1/2 -translate-y-1/2 opacity-70" aria-hidden />
          <div className="relative mx-auto max-w-6xl px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="order-2 lg:order-1 ln-layers" data-reveal-group>
              <div className="ln-layer ln-layer--back"><span className="ln-layer-tag">{COPY.meetaihi.layers[0]}</span></div>
              <div className="ln-layer ln-layer--mid"><span className="ln-layer-tag">{COPY.meetaihi.layers[1]}</span></div>
              <div className="ln-layer ln-layer--front">
                <img src="/landing/aihi-portrait.webp" alt={COPY.meetaihi.portraitAlt} width={400} height={417} loading="lazy" decoding="async" />
                <span className="ln-layer-tag">{COPY.meetaihi.layers[2]}</span>
              </div>
            </div>
            <div className="order-1 lg:order-2" data-reveal>
              <SectionHead num={COPY.meetaihi.num} kind={COPY.meetaihi.kind} name={COPY.meetaihi.name} domain={COPY.meetaihi.domain} closed />
              <p className="ln-text mt-6 max-w-lg">{COPY.meetaihi.text}</p>
              <ul className="mt-6 space-y-3 max-w-lg">
                {COPY.meetaihi.features.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-3 text-[15px] leading-snug">
                    <span className="ln-feature-icon"><Icon size={15} /></span>
                    <span className="ln-muted pt-1.5">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ───────────── 04 OZON LAB: текст слева, панель справа ───────────── */}
        <section id="ozon" className="ln-sec ln-sec--blue ln-sec--right scroll-mt-20">
          <div className="ln-shape w-[560px] h-[560px] -right-48 top-1/2 -translate-y-1/2 opacity-60" aria-hidden />
          <div className="relative mx-auto max-w-6xl px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
            <div data-reveal>
              <SectionHead num={COPY.ozon.num} kind={COPY.ozon.kind} name={COPY.ozon.name} domain={COPY.ozon.domain} closed />
              <p className="ln-text mt-6 max-w-lg">{COPY.ozon.text}</p>
              <p className="ln-dim text-[14px] mt-5 max-w-lg flex items-start gap-2.5">
                <Images size={15} className="flex-none mt-[3px]" style={{ color: 'var(--ln-blue-light)' }} />
                <span>{COPY.ozon.mockup} <span className="ln-domain">{COPY.ozon.mockupDomain}</span></span>
              </p>
            </div>
            <div className="relative mx-auto w-full max-w-[400px]" data-reveal style={cssVars({ '--d': '0.1s' })}>
              <div className="ln-panel">
                <div className="ln-panel-bar"><i /><i /><i /><span className="ln-domain ml-2">{COPY.ozon.domain}</span></div>
                <ol className="ln-pipe px-5 py-4">
                  {COPY.ozon.steps.map((s, i) => (
                    <li key={s}>
                      <span className="ln-pipe-icon relative">
                        {i === 0 && <MessageSquare size={18} />}
                        {i === 1 && <Layers size={18} />}
                        {i === 2 && <Printer size={18} />}
                        {i === 3 && <Truck size={18} />}
                        <span className="ln-pipe-num">{i + 1}</span>
                      </span>
                      <span className="font-semibold text-[15px]">{s}</span>
                      <span className="ml-auto h-1.5 rounded-full" style={{ width: `${34 + i * 12}%`, maxWidth: 120, background: 'var(--ln-line-strong)' }} aria-hidden />
                    </li>
                  ))}
                </ol>
              </div>
              <figure className="absolute -right-3 -bottom-6 sm:-right-8 m-0 w-24 text-center">
                <img src="/landing/aihi-agde.webp" alt={COPY.ozon.stickerAlt} width={96} height={95} loading="lazy"
                     className="w-24 h-auto drop-shadow-[0_12px_24px_rgba(0,0,0,0.6)] -rotate-6 hover:rotate-0 transition-transform duration-300" />
                <figcaption className="ln-dim text-[11px] leading-tight mt-1">{COPY.ozon.stickerCaption}</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* ───────────── 05 DAILY LAB: тетрадка слева, текст справа ───────────── */}
        <section id="daily" className="ln-sec ln-sec--ice scroll-mt-20">
          <div className="relative mx-auto max-w-6xl px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="order-2 lg:order-1 mx-auto w-full max-w-[380px]" data-reveal style={cssVars({ '--d': '0.1s' })}>
              <div className="ln-notebook px-6 py-5 pl-[72px]">
                {COPY.daily.rows.map(({ icon: Icon, label, done, w }) => (
                  <div key={label} className="ln-notebook-row">
                    <span className={`ln-box ${done ? 'is-done' : ''}`}>{done && <Check size={11} strokeWidth={3} />}</span>
                    <Icon size={15} className="ln-dim flex-none" />
                    <span className="text-[14px] font-semibold">{label}</span>
                    <span className="ln-line" style={cssVars({ '--w': w })} aria-hidden />
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2" data-reveal>
              <SectionHead num={COPY.daily.num} kind={COPY.daily.kind} name={COPY.daily.name} domain={COPY.daily.domain} closed />
              <p className="ln-text mt-6 max-w-lg flex items-start gap-3">
                <NotebookPen size={18} className="flex-none mt-1" style={{ color: 'var(--ln-ice)' }} />
                {COPY.daily.text}
              </p>
            </div>
          </div>
        </section>

        {/* ───────────── ФУТЕР ───────────── */}
        <footer className="relative" style={{ borderTop: '1px solid var(--ln-line)' }}>
          <div className="mx-auto max-w-6xl px-5 sm:px-8 py-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="ln-display flex items-center gap-2.5 text-lg">
                <img src="/favicon/favicon-96x96.png" alt="" width={28} height={28} loading="lazy" className="w-7 h-7 rounded-md" />
                witelab
              </div>
              <p className="ln-muted text-sm mt-3">
                {COPY.footer.contactLabel}:{' '}
                <a
                  href={`mailto:${COPY.footer.contactEmail}`}
                  className="ln-tap underline decoration-dotted underline-offset-4 hover:decoration-solid"
                  style={{ color: 'var(--ln-text)' }}
                >
                  {COPY.footer.contactEmail}
                </a>
              </p>
            </div>
            <p className="ln-dim text-xs">{COPY.footer.copy}</p>
          </div>
        </footer>
      </main>
    </div>
  );
}
